/**
 * Lista de grupos seguráveis aceitos para classificação de vidas (members)
 * no produto Vida em Grupo PME — Anexo IV (Grupo Segurável), na mesma ordem.
 * Espelha a lista da sheet `CONFIG` do template oficial. `value` (EN) vai pro
 * back; `label` (PT) é mostrado ao corretor.
 *
 * As duas colunas do anexo vêm junto e espelham `INSURABLE_GROUP_POLICIES` do
 * back (`group-life-insured-group.ts`), que é quem decide o desvio:
 *  - `singleHiringAllowed`: "Permitida contratação única?";
 *  - `requiresUnderwriting`: "Intervenção Subscrição?".
 *
 * NOTA: orthogonal ao schema de coberturas. Antes vivia em
 * `coverages.catalog.ts` mas foi separada porque coverage catalog passou a
 * vir do InsureMO.
 */
export const INSURABLE_GROUPS = [
  {
    value: 'EMPLOYEES',
    label: 'Funcionário(s)',
    singleHiringAllowed: true,
    requiresUnderwriting: false,
  },
  {
    value: 'INTERNS',
    label: 'Estagiário(s)',
    singleHiringAllowed: true,
    requiresUnderwriting: false,
  },
  {
    value: 'MANAGERS',
    label: 'Gestor(es)',
    singleHiringAllowed: false,
    requiresUnderwriting: false,
  },
  {
    value: 'DIRECTORS',
    label: 'Diretor(es)',
    singleHiringAllowed: false,
    requiresUnderwriting: false,
  },
  {
    value: 'PRESIDENTS',
    label: 'Presidente(s)',
    singleHiringAllowed: false,
    requiresUnderwriting: false,
  },
  {
    value: 'YOUNG_APPRENTICES',
    label: 'Menor(es) aprendiz(es)',
    singleHiringAllowed: true,
    requiresUnderwriting: false,
  },
  {
    value: 'ALL_CATEGORIES',
    label:
      'Presidente(s), Diretor(es), Gestor(es), Funcionário(s), Estagiário(s), Menor(es) Aprendiz(es)',
    singleHiringAllowed: true,
    requiresUnderwriting: false,
  },
  {
    value: 'SERVICE_PROVIDERS',
    label: 'Prestador(es) de Serviço(s)',
    singleHiringAllowed: true,
    requiresUnderwriting: true,
  },
  {
    value: 'DRIVERS',
    label: 'Motorista(s)',
    singleHiringAllowed: true,
    requiresUnderwriting: true,
  },
] as const;

/**
 * Grupos fora do Anexo IV. Não são oferecidos, mas vidas antigas ainda podem
 * tê-los gravados — só servem para exibir o rótulo.
 */
export const LEGACY_INSURABLE_GROUPS = [
  {
    value: 'PRESIDENTS_DIRECTORS_MANAGERS',
    label: 'Presidente(s), Diretor(es), Gestor(es)',
  },
  { value: 'PARTNERS_AND_OFFICERS', label: 'Sócios e Dirigentes' },
] as const;

export type InsurableGroup = (typeof INSURABLE_GROUPS)[number]['value'];

const ALL_KNOWN_GROUPS = [...INSURABLE_GROUPS, ...LEGACY_INSURABLE_GROUPS];

export function resolveInsurableGroupLabel(value: string): string {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return '';
  const byValue = ALL_KNOWN_GROUPS.find(g => g.value === trimmed);
  if (byValue) return byValue.label;
  const byLabel = ALL_KNOWN_GROUPS.find(
    g => g.label.toLowerCase() === trimmed.toLowerCase()
  );
  return byLabel?.label ?? trimmed;
}

/**
 * Código EN de um grupo, aceitando também o rótulo PT (vidas que vêm do back
 * com o rótulo gravado). Valor desconhecido volta como está.
 */
export function resolveInsurableGroupCode(value: string): string {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return '';
  const known = ALL_KNOWN_GROUPS.find(
    g => g.value === trimmed || g.label.toLowerCase() === trimmed.toLowerCase()
  );
  return known?.value ?? trimmed;
}

/** O que o corretor precisa saber de um grupo ao selecioná-lo. */
export type InsurableGroupNotice = 'UNDER_ANALYSIS' | 'NO_SINGLE_HIRING' | null;

/**
 * Aviso de um grupo na seleção: intervenção da subscrição pesa mais que a
 * contratação única. Grupo legado ou desconhecido não gera aviso.
 */
export function resolveInsurableGroupNotice(
  value: string
): InsurableGroupNotice {
  const code = resolveInsurableGroupCode(value);
  const group = INSURABLE_GROUPS.find(g => g.value === code);
  if (!group) return null;
  if (group.requiresUnderwriting) return 'UNDER_ANALYSIS';
  if (!group.singleHiringAllowed) return 'NO_SINGLE_HIRING';
  return null;
}

/** Texto do aviso exibido ao selecionar o grupo. */
export function insurableGroupNoticeMessage(
  notice: InsurableGroupNotice
): string | null {
  if (notice === 'UNDER_ANALYSIS') {
    return 'Grupo sujeito a análise da seguradora: com vidas neste grupo, o seguro será encaminhado para a subscrição após o cálculo.';
  }
  if (notice === 'NO_SINGLE_HIRING') {
    return 'Este grupo não pode ser contratado sozinho: se todas as vidas estiverem em grupos assim, o seguro será encaminhado para a subscrição.';
  }
  return null;
}
